import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { usePreferences } from "../hooks/use-preferences";
import { cn } from "@/lib/utils";
import { usePreferencesStore } from "../store/preferences-store";
import type {
  CategoryPreferenceType,
  CategoryPreferenceAttribute,
} from "@/api/models/api-types";

const EMPTY_SELECTED_SIZES: CategoryPreferenceAttribute[] = [];

export const SizePreferencesSection: React.FC = () => {
  const { t } = useTranslation();
  const { categories, isCategoriesLoading } = usePreferences();

  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  const toggleType = (type: string) => {
    setExpandedTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Section Header */}
      <div className="flex flex-col gap-2">
        <h3 className="text-base md:text-lg font-semibold text-text-primary dark:text-text-primary">
          {t("preferences.sizePreferences.title")}
        </h3>
        <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
          {t("preferences.sizePreferences.description")}
        </p>
      </div>

      {/* Types List */}
      <div className="max-h-[360px] md:max-h-[520px] overflow-y-auto pr-1">
        <div className="flex flex-col gap-3">
          {isCategoriesLoading && (
            <div className="px-4 py-6 text-sm text-text-tertiary dark:text-text-tertiary">
              {t("common.loading")}
            </div>
          )}
          {categories.map((categoryType) => (
            <TypeSizeCard
              key={categoryType.type}
              categoryType={categoryType}
              isExpanded={expandedTypes.has(categoryType.type)}
              onToggle={() => toggleType(categoryType.type)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface TypeSizeCardProps {
  categoryType: CategoryPreferenceType;
  isExpanded: boolean;
  onToggle: () => void;
}

const TypeSizeCard: React.FC<TypeSizeCardProps> = ({
  categoryType,
  isExpanded,
  onToggle,
}) => {
  const { t } = useTranslation();

  // Get unique sizes from attributes (remove duplicates by attributeValueId)
  const uniqueAttributes = React.useMemo(() => {
    const seen = new Set<string>();
    return categoryType.attributes.filter((attr) => {
      if (seen.has(attr.attributeValueId)) {
        return false;
      }
      seen.add(attr.attributeValueId);
      return true;
    });
  }, [categoryType.attributes]);

  // Store actions and state
  const selectedSizesFromStore = usePreferencesStore(
    (state) => state.sizePreferences[categoryType.type]
  );
  const selectedSizes = selectedSizesFromStore || EMPTY_SELECTED_SIZES;
  const addTypeSize = usePreferencesStore((state) => state.addTypeSize);
  const removeTypeSize = usePreferencesStore((state) => state.removeTypeSize);
  const clearTypeSizes = usePreferencesStore((state) => state.clearTypeSizes);

  const hasSelectedSizes = selectedSizes.length > 0;
  const isSizeSelected = (attributeValueId: string) =>
    selectedSizes.some((s) => s.attributeValueId === attributeValueId);

  // Translate type name using i18n
  const translatedTypeName = t(`preferences.categories.${categoryType.type}`, {
    defaultValue: categoryType.type,
  });

  return (
    <div className="border border-border-secondary dark:border-border-secondary rounded-xl overflow-hidden bg-white dark:bg-gray-800">
      {/* Type Header */}
      <div className="w-full flex items-center justify-between px-4 py-3 md:py-4 hover:bg-bg-tertiary dark:hover:bg-gray-700 transition-colors">
        <button
          onClick={onToggle}
          className="flex items-center gap-3 flex-1"
        >
          <span className="text-sm md:text-base font-semibold text-text-primary dark:text-text-primary">
            {translatedTypeName}
          </span>
          {hasSelectedSizes && (
            <span className="px-2 py-0.5 text-xs font-medium text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-full">
              {t("preferences.sizePreferences.sizesSelected", {
                count: selectedSizes.length,
              })}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {hasSelectedSizes && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearTypeSizes(categoryType.type);
              }}
              className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              aria-label={t("preferences.sizePreferences.clearSizes")}
            >
              <X className="w-4 h-4 text-red-500 dark:text-red-400" />
            </button>
          )}
          <button onClick={onToggle} className="p-1">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-text-tertiary dark:text-text-tertiary" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-tertiary" />
            )}
          </button>
        </div>
      </div>

      {/* Type Sizes (Expanded) */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-secondary dark:border-border-secondary bg-bg-tertiary dark:bg-gray-700/50">
          <div className="pt-4 flex flex-wrap gap-2 md:gap-3">
            {uniqueAttributes.length > 0 ? (
              uniqueAttributes.map((attr) => {
                const selected = isSizeSelected(attr.attributeValueId);
                return (
                  <label
                    key={attr.attributeValueId}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                      selected
                        ? "border-indigo-400 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                        : "border-border-secondary dark:border-border-secondary bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500"
                    )}
                  >
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => {
                        if (selected) {
                          removeTypeSize(
                            categoryType.type,
                            attr.attributeValueId
                          );
                        } else {
                          addTypeSize(categoryType.type, attr);
                        }
                      }}
                      className="border-indigo-400 dark:border-indigo-400"
                    />
                    <span
                      className={cn(
                        "text-sm font-medium",
                        selected
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-text-secondary dark:text-gray-300"
                      )}
                    >
                      {attr.value}
                    </span>
                  </label>
                );
              })
            ) : (
              <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
                {t("preferences.sizePreferences.noSizesAvailable")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
